import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { ArrowRight, Keyboard, Search } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { IconButton } from '../../components/ui/IconButton';
import { Tooltip } from '../../components/ui/Tooltip';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Text, typography } from '../../components/ui/Text';
import { cx } from '../../components/ui/cx';
import { useT } from '../i18n';
import { SearchResultItem } from './SearchResultItem';
import { resultTypeKey, type SearchHit } from './searchModel';

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
  term: string;
  onTermChange: (term: string) => void;
  /** Top results (already limited by the container). */
  hits: SearchHit[];
  loading?: boolean;
  onSelect: (hit: SearchHit) => void;
  onViewAll: () => void;
}

function groupByType(hits: SearchHit[]): [string, SearchHit[]][] {
  const groups = new Map<string, SearchHit[]>();
  for (const hit of hits) {
    const list = groups.get(hit.type) ?? [];
    list.push(hit);
    groups.set(hit.type, list);
  }
  return [...groups.entries()];
}

/**
 * Command-palette quick search on the design system: a react-aria modal
 * anchored near the top, an autofocused input, results grouped by type with
 * arrow-key navigation, and a "View all results" footer. Pure props — the
 * container owns querying and navigation.
 */
export const SearchPalette = ({
  open,
  onClose,
  term,
  onTermChange,
  hits,
  loading = false,
  onSelect,
  onViewAll,
}: SearchPaletteProps) => {
  const t = useT();
  // `hits` is the flat navigation order; groups are only for display.
  const [highlight, setHighlight] = useState(0);
  const groups = useMemo(() => groupByType(hits), [hits]);
  const groupLabel = (type: string) => {
    const key = resultTypeKey(type);
    return key ? t(key) : type;
  };

  // Reset the highlight to the top whenever the result set changes.
  useEffect(() => setHighlight(0), [hits]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (hits.length) setHighlight(h => (h + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (hits.length) setHighlight(h => (h - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[highlight];
      if (hit) onSelect(hit);
      else onViewAll();
    } else if (e.key === 'Escape') {
      // A native `type=search` input consumes Escape (to clear itself), which
      // otherwise swallows react-aria's Esc-to-dismiss; close explicitly.
      e.preventDefault();
      onClose();
    }
  };

  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
      isDismissable
      // z-index sits above the stock mobile sidebar overlay (MUI
      // `zIndex.snackbar` = 1400); the palette is opened from inside that
      // drawer on mobile, so a lower z-index would render it hidden behind it.
      className="fixed inset-0 z-[1500] flex items-start justify-center bg-overlay p-4 pt-[12vh]"
    >
      <Modal className="w-full max-w-xl rounded-xl border border-default bg-surface shadow-lg outline-none">
        <Dialog
          aria-label={t('search.palette.ariaLabel')}
          className="flex flex-col outline-none"
        >
          <div className="flex items-center gap-2 border-b border-default px-4 py-3">
            <span className="inline-flex text-fg-muted">
              <Icon as={Search} size="md" />
            </span>
            {/* react-aria autofocuses the first focusable element in a Modal. */}
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              type="search"
              role="searchbox"
              aria-label={t('search.palette.ariaLabel')}
              placeholder={t('search.palette.placeholder')}
              value={term}
              onChange={e => onTermChange(e.target.value)}
              onKeyDown={onKeyDown}
              className={cx(
                typography.body,
                'flex-1 appearance-none border-0 bg-transparent text-fg outline-none placeholder:text-fg-muted',
                // Suppress the native (blue) search clear button — design system
                // forbids default browser chrome/colors.
                '[&::-webkit-search-cancel-button]:appearance-none',
              )}
            />
            <Tooltip content={t('search.palette.shortcut')}>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('search.palette.shortcut')}
              >
                <Icon as={Keyboard} size="sm" />
              </IconButton>
            </Tooltip>
          </div>

          <div className="max-h-[50vh] overflow-y-auto px-2 pb-2 pt-3">
            {loading && <Spinner center label={t('search.common.searching')} />}
            {!loading && hits.length === 0 && (
              <EmptyState
                icon={<Icon as={Search} size="lg" />}
                title={
                  term
                    ? t('search.palette.emptyTitleNoResults')
                    : t('search.palette.emptyTitleStart')
                }
                description={
                  term
                    ? t('search.palette.emptyDescriptionNoResults')
                    : t('search.palette.emptyDescriptionStart')
                }
              />
            )}
            {!loading &&
              hits.length > 0 &&
              groups.map(([type, list]) => (
                <div key={type} className="mb-2 last:mb-0">
                  <Text
                    variant="overline"
                    tone="tertiary"
                    className="block px-3 pb-2 pt-1"
                  >
                    {groupLabel(type)}
                  </Text>
                  {list.map(hit => (
                    <SearchResultItem
                      key={`${hit.type}:${hit.location}`}
                      hit={hit}
                      highlighted={hits[highlight] === hit}
                      onSelect={onSelect}
                    />
                  ))}
                </div>
              ))}
          </div>

          {/* Footer "View all results" button.
              appearance-none resets the browser's native grey-bg chrome
              (button { appearance: auto } applies #efefef in light/dark).
              bg-transparent is the explicit base background so the button
              never falls back to the native colour; hover/focus-visible then
              elevate to bg-muted, matching SearchResultItem's pattern. */}
          <button
            type="button"
            onClick={onViewAll}
            className={cx(
              typography.label,
              'flex w-full appearance-none items-center justify-between gap-2 border-t border-default bg-transparent px-4 py-3 text-left text-fg-secondary outline-none transition hover:bg-muted focus-visible:bg-muted',
            )}
          >
            <span>
              {term
                ? t('search.palette.viewAllFor', { term })
                : t('search.palette.viewAll')}
            </span>
            <Icon as={ArrowRight} size="sm" />
          </button>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
